import React, { useEffect } from 'react';
import { formatPhoneNumber } from 'react-phone-number-input';

import { VerificationCodeStatus } from 'api/curo/VerificationProcessApi';
import FormTitle from 'components/PageLayout/FormTitle';
import LoaderOverlay from 'components/PageLayout/LoaderOverlay';
import AuthCodeInput from 'components/curo-components/common/AuthCodeInput';
import { GoogleAnalyticsEventType } from 'enums/GoogleAnalyticsEventType';
import { HttpStatusCode } from 'enums/HttpStatusCode';
import { getPhoneVerificationData, getSignInProcessState } from 'handlers/curo-handlers/selectors';
import { useAppSelector } from 'hooks/reduxHooks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useGoogleAnalytics from 'hooks/useGoogleAnalytics';
import { useLoadingState } from 'hooks/useLoadingState';
import styles from 'pages/CuroPages/ApplicationProcess/steps/VerificationProcessStep/VerificationProcessStep.module.scss';
import { IDefaultStepProps } from 'pages/CuroPages/types';
import { sendPhoneVerificationCode, verifyPhoneVerificationCode } from 'thunks';

interface IPhoneVerificationStepProps extends IDefaultStepProps {
  onStepFailed: () => void;
}

const PAGE_TITLE = 'Phone Verification';

const PhoneVerificationStep = ({ onStepComplete, onStepFailed }: IPhoneVerificationStepProps) => {
  const phoneVerificationData = useAppSelector(getPhoneVerificationData);
  const [isPageLoading, setIsPageLoading] = useLoadingState();
  const { phone, accountData } = useAppSelector(getSignInProcessState);
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const trackEvent = useGoogleAnalytics();

  const formattedPhoneNumber = formatPhoneNumber(phone);

  const handleSendPhoneVerificationCode = async () => {
    try {
      if (accountData?.currentApplicationId) {
        if (isPageLoading === 'idle') {
          setIsPageLoading('loading');
        }

        const { status } = await dispatchWithUnwrap(
          sendPhoneVerificationCode({
            applicationId: accountData.currentApplicationId,
            phone: phone,
          }),
        );

        setIsPageLoading('success');

        if (status === VerificationCodeStatus.Canceled) {
          onStepFailed();
          return;
        }
      }
    } catch (error) {
      if (error.responseStatus === HttpStatusCode.TooManyRequests) {
        return;
      }

      throw error;
    }
  };

  useEffect(() => {
    handleSendPhoneVerificationCode();
    trackEvent(GoogleAnalyticsEventType.ApplyProcessPhoneVerify);
  }, []);

  const handleVerifyCode = async (code: string) => {
    if (accountData?.currentApplicationId) {
      const { status } = await dispatchWithUnwrap(
        verifyPhoneVerificationCode({
          applicationId: accountData.currentApplicationId,
          code,
        }),
      );

      if (status === VerificationCodeStatus.Canceled) {
        onStepFailed();
        return;
      }

      if (status === VerificationCodeStatus.Approved) {
        onStepComplete();
        return;
      }
    }
  };

  if (isPageLoading === 'loading' || isPageLoading === 'idle' || !phoneVerificationData) {
    return <LoaderOverlay />;
  }

  const { sendingAttemptsLeft } = phoneVerificationData;

  return (
    <>
      <FormTitle
        title={PAGE_TITLE}
        subTitle={
          <p className={styles.stepDescription}>
            <span>
              Now that your email is verified, please verify your phone number. We have sent a
              6-digit verification code to <strong>{formattedPhoneNumber}</strong>. Please enter the
              code below.
            </span>
          </p>
        }
      />
      <AuthCodeInput
        handleVerifyCode={handleVerifyCode}
        handleResendCode={handleSendPhoneVerificationCode}
        buttonTitle="Continue"
        sendingAttemptsLeft={sendingAttemptsLeft}
      />
    </>
  );
};

export default PhoneVerificationStep;
